import { useState, Fragment, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { motion } from "framer-motion"
import { X, CheckCircle, AlertTriangle, XCircle, Clock, Check, Activity, Loader2 } from 'lucide-react'

import transferHistoryService from "../../services/transferHistoryService"

// Status Option Card Component
const StatusOptionCard = ({ id, title, description, icon, color, selected, onClick }) => {
  const colorClasses = {
    green: {
      selected: "border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-500",
      icon: "bg-green-100 dark:bg-green-800/40 text-green-600 dark:text-green-400",
      text: "text-green-700 dark:text-green-400",
      dot: "bg-green-500",
    },
    yellow: {
      selected: "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-500",
      icon: "bg-yellow-100 dark:bg-yellow-800/40 text-yellow-600 dark:text-yellow-400",
      text: "text-yellow-700 dark:text-yellow-400",
      dot: "bg-yellow-500",
    },
    orange: {
      selected: "border-orange-500 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-500",
      icon: "bg-orange-100 dark:bg-orange-800/40 text-orange-600 dark:text-orange-400",
      text: "text-orange-700 dark:text-orange-400",
      dot: "bg-orange-500",
    },
    red: {
      selected: "border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-500",
      icon: "bg-red-100 dark:bg-red-800/40 text-red-600 dark:text-red-400",
      text: "text-red-700 dark:text-red-400",
      dot: "bg-red-500",
    },
  }

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className={`relative overflow-hidden rounded-xl p-3 cursor-pointer transition-all border ${selected
        ? colorClasses[color].selected
        : "bg-white dark:border-neutral-900 dark:bg-neutral-900/30 border-neutral-200 dark:bg-neutral-800 hover:border-blue-300 dark:hover:border-blue-700"
        }`}
      onClick={() => onClick(id)}
    >
      <div className="flex items-center gap-3">
        <div
          className={`p-2 rounded-full ${selected
            ? colorClasses[color].icon
            : "bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"
            }`}
        >
          {icon}
        </div>
        <div className="flex-1 text-start">
          <h3
            className={`text-sm font-medium ${selected ? colorClasses[color].text : "text-neutral-700 dark:text-neutral-300"
              }`}
          >
            {title}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
        </div>
        <div
          className={`w-4 h-4 rounded-full flex items-center justify-center ${selected ? colorClasses[color].dot : "border border-neutral-300 dark:border-neutral-600"
            }`}
        >
          {selected && <Check size={10} className="text-white" />}
        </div>
      </div>
    </motion.div>
  )
}

const ChangeStatusModal = ({ isOpen, onClose, transaction, onSuccess }) => {
  const [selectedStatus, setSelectedStatus] = useState("")
  const [notes, setNotes] = useState("")
  const [type, setType] = useState("")

  const [rejectionReason, setRejectionReason] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (isOpen && transaction) {
      setSelectedStatus(transaction?.status || "")
      setType(transaction?.type || "")
      setNotes("")
      setRejectionReason("")
      setIsProcessing(false)
      setIsComplete(false)
      setError(null)
    }
  }, [isOpen, transaction])

  // Status options
  const statusOptions = [
    {
      id: "completed",
      title: "Completed",
      description: "Transaction has been successfully processed",
      icon: <CheckCircle size={16} />,
      color: "green",
    },
    {
      id: "pending",
      title: "Pending",
      description: "Transaction is awaiting processing",
      icon: <Clock size={16} />,
      color: "yellow",
    },
    {
      id: "rejected",
      title: "Rejected",
      description: "Transaction has been rejected",
      icon: <XCircle size={16} />,
      color: "red",
    },
    {
      id: "reversed",
      title: "Reversed",
      description: "Transaction has been reversed",
      icon: <AlertTriangle size={16} />,
      color: "orange",
    },
  ]



  // Handle submit
  const handleSubmit = async () => {
    if (!selectedStatus) {
      setError("Please select a status")
      return
    }
    if (
      selectedStatus === "rejected" &&
      (type === "Top-Down" || type === "Top-Up") &&
      !rejectionReason.trim()
    ) {
      setError("Please provide a rejection reason")
      return
    }
    setIsProcessing(true)

    try {
      const response = await transferHistoryService.changeStatus(transaction._id, {
        status: selectedStatus,
        notes: notes,
        rejectionReason: selectedStatus === "rejected" ? rejectionReason : undefined,

      })

      if (response.data && response.data.statusCode === 200) {
        setIsProcessing(false)
        setIsComplete(true)

        // After 1.5 seconds, close the modal and trigger success callback
        setTimeout(() => {
          onSuccess()
          onClose()
        }, 1500)
      } else {
        throw new Error("Failed to update status")
      }
    } catch (error) {
      console.error("Error updating transaction status:", error)
      setIsProcessing(false)
      setError(error.response?.data?.message || "An error occurred while updating the status")
    }
  }

  // Check if form is valid
  const isFormValid = () => {
    if (!selectedStatus) return false

    if (
      selectedStatus === "rejected" &&
      (type === "Top-Down" || type === "Top-Up") &&
      !rejectionReason.trim()
    ) {
      return false
    }

    return true
  }
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  }

  // Render processing state
  const renderProcessingState = () => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 size={40} className="mx-auto mb-4 text-blue-600 dark:text-blue-400" />
        </motion.div>
        <h3 className="text-lg font-medium text-neutral-800 dark:text-neutral-200 mb-2">Updating Transaction</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-xs mx-auto">
          Please wait while we update the transaction status...
        </p>
        <div className="w-full max-w-xs mx-auto h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 2 }}
            className="h-full bg-blue-500"
          />
        </div>
      </motion.div>
    )
  }

  // Render completion state
  const renderCompletionState = () => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 10 }}
          className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center"
        >
          <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
        </motion.div>
        <h3 className="text-lg font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          Status Updated Successfully!
        </h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-xs mx-auto">
          The transaction status has been updated to {selectedStatus}.
        </p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800/30 mb-4 mx-auto max-w-xs"
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-700 dark:text-green-400 text-left">
              Changes have been logged
            </span>
          </div>
          <div className="text-sm text-green-600 dark:text-green-300 text-left">
            Transaction history has been updated
          </div>
        </motion.div>
      </motion.div>
    )
  }

  // Render error state
  const renderErrorState = () => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 10 }}
          className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center"
        >
          <XCircle size={32} className="text-red-600 dark:text-red-400" />
        </motion.div>
        <h3 className="text-lg font-medium text-neutral-800 dark:text-neutral-200 mb-2">Update Failed</h3>
        <p className="text-sm text-red-600 dark:text-red-400 mb-6">{error}</p>
        <div className="flex flex-col gap-3 justify-center max-w-xs mx-auto">
          <button
            onClick={() => setError(null)}
            className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-xl transition-colors text-sm font-medium"
          >
            Try Again
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 border border-neutral-300 dark:bg-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-800 dark:text-neutral-200 rounded-xl transition-colors text-sm font-medium"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    )
  }

  // Render main form
  const renderForm = () => {
    return (
      <motion.div
        variants={containerVariants}
        initial={false}
        animate="visible"
        className="space-y-6"
      >
        {/* Transaction info */}
        <div className="p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:bg-neutral-800">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-800/40 text-blue-600 dark:text-blue-400 rounded-full">
              <Activity size={16} />
            </div>
            <div className="text-start">
              <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                Transaction #{transaction?.transaction_id}
              </h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {transaction?.createdAt && new Date(transaction.createdAt).toLocaleString()}
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-neutral-500 dark:text-neutral-400 mr-2">Current Status:</span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${transaction?.status === "completed"
                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-500"
                    : transaction?.status === "pending"
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500"
                      : transaction?.status === "failed" || transaction?.status === "rejected"
                        ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-500"
                        : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-500"
                    }`}
                >
                  <span
                    className={`w-1.5 h-1.5 mr-1 rounded-full ${transaction?.status === "completed"
                      ? "bg-green-500"
                      : transaction?.status === "pending"
                        ? "bg-yellow-500"
                        : transaction?.status === "failed" || transaction?.status === "rejected"
                          ? "bg-red-500"
                          : "bg-orange-500"
                      }`}
                  ></span>
                  {transaction?.status && transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Status options */}
        <motion.div variants={itemVariants}>
          <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200 text-start">
            Select New Status
          </label>
          <div className="space-y-3">
            {statusOptions.map((option) => (
              <StatusOptionCard
                key={option.id}
                id={option.id}
                title={option.title}
                description={option.description}
                icon={option.icon}
                color={option.color}
                selected={selectedStatus === option.id}
                onClick={setSelectedStatus}
              />
            ))}
          </div>
        </motion.div>

        {/* Warning for certain status changes */}
        {(selectedStatus === "rejected" || selectedStatus === "reversed") && (
          <motion.div
            variants={itemVariants}
            className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl flex items-start gap-3"
          >
            <AlertTriangle size={20} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div className="text-start">
              <h4 className="text-sm font-medium text-red-700 dark:text-red-400">Warning</h4>
              <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                {selectedStatus === "failed"
                  ? "Rejecting this transaction may trigger notifications and affect account balances."
                  : "Reversing this transaction will create a new transaction in the opposite direction and may affect account balances."}
              </p>
            </div>
          </motion.div>
        )}
        {selectedStatus === "rejected" && (type === "Top-Down" || type === "Top-Up")  && (
          <motion.div variants={itemVariants} className="text-start">
            <label className="block mb-2 text-sm font-medium text-red-700 dark:text-red-400">
              Rejection Reason *
            </label>

            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Explain why the withdrawal was rejected (e.g., IBAN mismatch, incorrect amount, unsupported bank...)"
              rows={3}
              className="w-full border p-3 text-sm border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 rounded-xl placeholder-neutral-500 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
          </motion.div>
        )}

        {/* Notes */}
        <motion.div variants={itemVariants} className="text-start">
          <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Additional Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional information about this change..."
            rows={3}
            className="w-full border p-3 text-sm border-neutral-200 dark:bg-neutral-800 rounded-xl placeholder-neutral-500 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </motion.div>
      </motion.div>
    )
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                {/* Header */}
                <div className="relative p-6 border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white">
                      Update Transaction Status
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {isProcessing
                    ? renderProcessingState()
                    : isComplete
                      ? renderCompletionState()
                      : error
                        ? renderErrorState()
                        : renderForm()}
                </div>

                {/* Footer */}
                {!isProcessing && !isComplete && !error && (
                  <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-6 flex justify-between">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 h-10 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!isFormValid()}
                      className="px-5 h-10 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-xl transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      Update Status
                      <Check size={16} />
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ChangeStatusModal
